import React from 'react';
import { Col, Form, Row, Space, Typography } from 'antd';
import styled from 'styled-components';

import { SaveOutlined } from '@ant-design/icons';

import { useGetAllClients } from '../../common/useGetAllClients';
import { useGetAllProjects } from '../../common/useGetAllProjects';
import { StyledButton } from '../../components/Button';
import { FormSelectMultiple } from '../../components/forms/FormSelectMultiple';

import { ConsentType, consentTypeMap, consentTypes } from './Consents.types';

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 576px) {
    flex-direction: row;
  }
`;

export type AddCandidateConsentsFormValues = {
  consentTypes: ConsentType[];
  clients?: string[];
  projects?: string[];
};

export const AddCandidateConsentsForm: React.FC<{
  initialConsentTypes?: ConsentType[];
  onSubmit(values: AddCandidateConsentsFormValues): void;
  onCancel(): void;
}> = ({ initialConsentTypes, onSubmit, onCancel }) => {
  const { data: clients } = useGetAllClients();
  const { data: projects } = useGetAllProjects();

  const [form] = Form.useForm<AddCandidateConsentsFormValues>();
  const selectedConsentTypes = Form.useWatch('consentTypes', form) || [];

  return (
    <Form
      form={form}
      colon={false}
      layout="vertical"
      initialValues={initialConsentTypes?.length ? { consentTypes: initialConsentTypes } : undefined}
      onFinish={onSubmit}
      style={{ padding: '0 15px' }}
      requiredMark={false}
    >
      <Row gutter={[10, 5]}>
        <Col xs={24}>
          <StyledHeader>
            <Typography.Title level={5}>Adding consent</Typography.Title>

            <Space style={{ marginLeft: 'auto' }}>
              <StyledButton onClick={onCancel}>Cancel</StyledButton>
              <StyledButton htmlType="submit" type="primary" icon={<SaveOutlined />}>
                Save
              </StyledButton>
            </Space>
          </StyledHeader>
        </Col>
        <Col xs={24} md={12}>
          <FormSelectMultiple
            required
            name="consentTypes"
            label="Consent types"
            mode="multiple"
            options={consentTypes.map((consentType) => ({
              value: consentType,
              content: consentTypeMap[consentType],
            }))}
            rules={[{ type: 'array', required: true, min: 1, message: 'Select at least one consent type' }]}
          />

          {selectedConsentTypes.includes('CLIENT_DATA_PROCESSING') && (
            <FormSelectMultiple
              required
              name="clients"
              label="Clients"
              mode="multiple"
              options={clients?.results.map(({ uuid, name }) => ({ value: uuid, content: name })) ?? []}
              dependencies={['consentTypes']}
              preserve={false}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, selectedClients) {
                    const isRequired = (getFieldValue('consentTypes') || []).includes('CLIENT_DATA_PROCESSING');
                    if (!isRequired) return Promise.resolve();

                    return Array.isArray(selectedClients) && selectedClients.length > 0
                      ? Promise.resolve()
                      : Promise.reject(new Error('Select at least one client'));
                  },
                }),
              ]}
            />
          )}

          {selectedConsentTypes.includes('PROJECT_PARTICIPATION') && (
            <FormSelectMultiple
              required
              name="projects"
              label="Projects"
              mode="multiple"
              options={projects?.results.map(({ uuid, name }) => ({ value: uuid, content: name })) ?? []}
              dependencies={['consentTypes']}
              preserve={false}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, selectedProjects) {
                    const isRequired = (getFieldValue('consentTypes') || []).includes('PROJECT_PARTICIPATION');
                    if (!isRequired) return Promise.resolve();

                    return Array.isArray(selectedProjects) && selectedProjects.length > 0
                      ? Promise.resolve()
                      : Promise.reject(new Error('Select at least one project'));
                  },
                }),
              ]}
            />
          )}
        </Col>
      </Row>
    </Form>
  );
};
